import { useRef, useState } from 'react'
import { Paperclip, Download, Trash2, Plus } from 'lucide-react'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog } from '@/components/ui/dialog'
import { useToast } from '@/components/ui/toast'

/**
 * Gerencia anexos (contrato ou aditivo).
 *
 * Props:
 *  - anexos: array de { <idKey>, nome, formato, tamanho, cadastradoEm }
 *  - idKey: 'contratoAnexoID' | 'aditivoAnexoID'
 *  - uploadUrl: ex. `/contratos/5/anexos`
 *  - itemBase: ex. `/contrato-anexos` (usado para download/delete por id)
 *  - reload: recarrega o grafo do contrato
 *  - compact: layout reduzido (usado nos aditivos)
 */
export default function AnexoManager({ anexos = [], idKey, uploadUrl, itemBase, reload, compact = false }) {
  const toast = useToast()
  const fileRef = useRef(null)
  const [pending, setPending] = useState(null) // arquivos selecionados aguardando nomeação
  const [uploading, setUploading] = useState(false)
  const [busyId, setBusyId] = useState(null)

  function onPick(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = '' // permite reselecionar os mesmos arquivos
    if (files.length === 0) return
    setPending(files.map((file) => ({ file, nome: stripExt(file.name) })))
  }

  async function confirmUpload() {
    if (!pending || pending.length === 0) return
    setUploading(true)
    try {
      const fd = new FormData()
      pending.forEach((p) => {
        fd.append('files[]', p.file)
        fd.append('nomes[]', p.nome || stripExt(p.file.name))
      })
      await api.post(uploadUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setPending(null)
      await reload()
      toast.success(pending.length > 1 ? 'Anexos enviados.' : 'Anexo enviado.')
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao enviar anexos.')
    } finally {
      setUploading(false)
    }
  }

  async function download(anexo) {
    setBusyId(anexo[idKey])
    try {
      const res = await api.get(`${itemBase}/${anexo[idKey]}/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = anexo.nome || 'anexo'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Erro ao baixar o anexo.')
    } finally {
      setBusyId(null)
    }
  }

  async function remove(anexo) {
    if (!window.confirm(`Excluir o anexo "${anexo.nome}"?`)) return
    setBusyId(anexo[idKey])
    try {
      await api.delete(`${itemBase}/${anexo[idKey]}`)
      await reload()
      toast.success('Anexo excluído.')
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao excluir o anexo.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className={compact ? 'mt-2' : 'mt-4'}>
      <div className={`flex items-center mb-2 ${compact ? 'justify-start gap-2' : 'justify-between'}`}>
        <span className={compact ? 'text-xs font-medium text-muted-foreground flex items-center gap-1.5' : 'text-sm font-semibold flex items-center gap-2'}>
          <Paperclip className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} /> Anexos {anexos.length > 0 && `(${anexos.length})`}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
          <Plus className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} /> Adicionar
        </Button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={onPick} />
      </div>

      {anexos.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nenhum anexo.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {anexos.map((a) => (
            <li key={a[idKey]} className="flex items-center gap-3 px-3 py-2 text-sm">
              <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="flex-1 truncate" title={a.nome}>{a.nome}</span>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{formatSize(a.tamanho)}</span>
              <button type="button" title="Baixar" disabled={busyId === a[idKey]} onClick={() => download(a)}
                className="p-1.5 rounded-md hover:bg-accent/60 text-muted-foreground hover:text-foreground disabled:opacity-50">
                <Download className="h-4 w-4" />
              </button>
              <button type="button" title="Excluir" disabled={busyId === a[idKey]} onClick={() => remove(a)}
                className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-50">
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Modal de nomeação dos anexos selecionados */}
      <Dialog
        open={!!pending}
        onClose={() => (uploading ? null : setPending(null))}
        title={pending && pending.length > 1 ? 'Nomear anexos' : 'Nomear anexo'}
        description="Defina um nome para cada arquivo antes de enviar."
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setPending(null)} disabled={uploading}>Cancelar</Button>
            <Button onClick={confirmUpload} disabled={uploading}>{uploading ? 'Enviando...' : 'Enviar'}</Button>
          </>
        }
      >
        <div className="space-y-3">
          {(pending || []).map((p, i) => (
            <div key={i} className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Paperclip className="h-3.5 w-3.5" />
                <span className="truncate">{p.file.name}</span>
                <span className="whitespace-nowrap">· {formatSize(p.file.size / (1024 * 1024))}</span>
              </div>
              <Input
                value={p.nome}
                placeholder="Nome do anexo"
                onChange={(e) => {
                  const v = e.target.value
                  setPending((prev) => prev.map((x, j) => (j === i ? { ...x, nome: v } : x)))
                }}
              />
            </div>
          ))}
        </div>
      </Dialog>
    </div>
  )
}

function stripExt(name) {
  return String(name).replace(/\.[^.]+$/, '')
}

function formatSize(mb) {
  const n = Number(mb || 0)
  if (n < 1) return `${Math.round(n * 1024)} KB`
  return `${n.toFixed(2)} MB`
}
